import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  MenuItem,
  TextField
} from '@mui/material'
import { GreenText, LabelField } from 'components'
import { DateField, TimeField } from 'components/fields'
import * as controllers from 'controllers'
import { CompanyAddressDto, CompanyDto, MichiganTitleApplicationCoverLetterDto, miTitleApplicationCoverLetterValidationSchema, PersonDto } from 'dtos'
import { Form, Formik, setNestedObjectValues } from 'formik'
import { useLoadingState } from 'hooks'
import { AUTOCOMPLETE_PAGESIZE, DEBOUNCE_DELAY, Junction } from 'localConstants'
import { useEffect, useState } from 'react'
import { formatAddress, getCompaniesAutocompleteBase, getComparableAddress, getComparableCompany, getComparablePerson, useDebounce } from 'utils'

interface MichiganSOSBundlesTitleApplicationCoverLetterEditDialogProps {
  initialValues: MichiganTitleApplicationCoverLetterDto
  isLoading: boolean
  open: boolean
  onClose: () => void
  onSave: (values: MichiganTitleApplicationCoverLetterDto) => void
}

export default function MichiganSOSBundlesTitleApplicationCoverLetterEditDialog({
  initialValues,
  isLoading,
  onClose,
  onSave,
  open
}: MichiganSOSBundlesTitleApplicationCoverLetterEditDialogProps) {
  const [loadingState, setLoadingState] = useLoadingState({
    isGettingMISOSBranches: false,
    isGettingMISOSBranchAddresses: false
  })

  const [contactPeople, setContactPeople] = useState<PersonDto[]>([])

  const [michiganSoSBranchCompanies, setMichiganSoSBranchCompanies] = useState<CompanyDto[]>([])
  const [michiganSoSBranchCompanyAddresses, setMichiganSoSBranchCompanyAddresses] = useState<CompanyAddressDto[]>([])

  const getMISOSBranchesAutocomplete = useDebounce((search: string) => {
    setLoadingState('isGettingMISOSBranches', true)
    getCompaniesAutocompleteBase(search, ['isActive', 'IsMichiganSOSBranch'])
      .then(response => {
        setMichiganSoSBranchCompanies(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingMISOSBranches', false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    controllers
      .getPeople({
        page: 0,
        pageSize: 100,
        permission: 'TITLES_ADD_EDIT',
        statuses: ['isActive'],
        order: 'asc',
        orderBy: 'name'
      })
      .then(res => {
        setContactPeople(res.value)
      })
  }, [])

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize
        initialValues={{
          ...initialValues,
          description: initialValues.description || 'Canadian Ownership to Michigan Title.'
        }}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnBlur={true}
        validateOnChange={true}
        validationSchema={miTitleApplicationCoverLetterValidationSchema}
      >
        {({ errors, handleBlur, handleChange, isSubmitting, setFieldValue, setErrors, setTouched, touched, validateForm, values }) => {
          return (
            <Form>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>MI Title Application Cover Letter</DialogTitle>

                  <Grid item xs={12}>
                    <DateField
                      disabled={isLoading || isSubmitting}
                      error={Boolean(errors.dropOffDate)}
                      helperText={errors.dropOffDate}
                      label='Drop Off Date'
                      onChange={value => setFieldValue('dropOffDate', value)}
                      value={values.dropOffDate}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TimeField
                      disabled={isLoading || isSubmitting}
                      error={Boolean(errors.dropOffTime)}
                      helperText={errors.dropOffTime}
                      label='Drop Off Time'
                      onChange={value => setFieldValue('dropOffTime', value)}
                      value={values.dropOffTime}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting}
                      disableListWrap
                      getOptionLabel={miSOSBranch => miSOSBranch.name!}
                      noOptionsText={
                        loadingState.isGettingMISOSBranches ? 'Searching MI SoS Branches...' : 'No MI SoS Branch. Start typing to search for MI SoS Branches...'
                      }
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('michiganSOSBranchCompany', value)
                        setFieldValue('michiganSOSBranchCompanyAddress', undefined)

                        if (value) {
                          setLoadingState('isGettingMISOSBranchAddresses', true)
                          controllers
                            .getCompanyAddresses(value?.companyGuid!, {
                              page: 0,
                              pageSize: AUTOCOMPLETE_PAGESIZE,
                              statuses: ['isActive', 'isMichiganSOSBranchAddress'],
                              junction: Junction.AND
                            })
                            .then(res => {
                              setMichiganSoSBranchCompanyAddresses(res.value)
                            })
                            .finally(() => {
                              setLoadingState('isGettingMISOSBranchAddresses', false)
                            })
                        }
                      }}
                      onInputChange={(_e, value, _reason) => {
                        getMISOSBranchesAutocomplete(value)
                      }}
                      options={michiganSoSBranchCompanies.map(company => getComparableCompany(company))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label='MI SoS Branch'
                          error={Boolean(touched.michiganSOSBranchCompany && errors.michiganSOSBranchCompany)}
                          helperText={touched.michiganSOSBranchCompany && errors.michiganSOSBranchCompany}
                          name='michiganSOSBranchCompany'
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingMISOSBranches && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                      value={values.michiganSOSBranchCompany ? getComparableCompany(values.michiganSOSBranchCompany) : null}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting}
                      disableListWrap
                      getOptionLabel={address => formatAddress(address)}
                      noOptionsText={loadingState.isGettingMISOSBranchAddresses ? 'Searching MI SoS Branch Addresses...' : 'No MI SoS Branch Addresses.'}
                      onBlur={handleBlur}
                      onChange={(_e, value) => setFieldValue('michiganSOSBranchCompanyAddress', value)}
                      options={michiganSoSBranchCompanyAddresses.map(address => getComparableAddress(address))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.michiganSOSBranchCompanyAddress && errors.michiganSOSBranchCompanyAddress)}
                          helperText={touched.michiganSOSBranchCompanyAddress && errors.michiganSOSBranchCompanyAddress}
                          name='michiganSOSBranchCompanyAddress'
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingMISOSBranchAddresses && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                          label='MI SoS Branch Address'
                        />
                      )}
                      value={values.michiganSOSBranchCompanyAddress ? getComparableAddress(values.michiganSOSBranchCompanyAddress) : null}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading || isSubmitting}
                      error={Boolean(touched.description && errors.description)}
                      helperText={touched.description && errors.description}
                      label='Description'
                      name='description'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.description || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading || isSubmitting}
                      error={Boolean(touched.contactPerson && errors.contactPerson)}
                      helperText={touched.contactPerson && errors.contactPerson}
                      label='Contact Person'
                      name='contactPerson'
                      onBlur={handleBlur}
                      onChange={e => setFieldValue('contactPerson', e.target.value ? JSON.parse(e.target.value) : undefined)}
                      select
                      value={values.contactPerson ? JSON.stringify(getComparablePerson(values.contactPerson)) : null}
                    >
                      {contactPeople.map(person => (
                        <MenuItem key={person.personGuid} value={JSON.stringify(getComparablePerson(person))}>
                          {person.firstName} {person.lastName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Mobile Phone' value={values.contactPerson?.mobilePhone} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Email' value={values.contactPerson?.email} />
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**Drop Off Date:**

Allows the user to select a date using a date picker.

The field is not populated by default.

This field is required.

**Time:**

Allows the user to enter a time manually.

The field is not populated by default.

This field is required.

**MI SoS Branch Drop-Down:**

Displays all active companies where the "Michigan SoS Branch" toggle is enabled under Company Details – Edit Company.

This field is required.

**MI SoS Branch Address:**

A cascading dropdown populated based on the MI SoS Branch selection.

Displays all addresses associated with the selected MI SoS Branch, where the "MI SoS Branch Address" toggle is enabled.

This field is required.

**Description:**

A free-text field.

By default, it is populated with “Canadian Ownership to Michigan Title.”

This field is required.

**Contact Person Drop-Down:**

Displays names of all users with VTS2 Access and "Titles Add/Edit" permissions.

This field is required.

**Mobile Phone:**

A read-only label auto-populated based on the selected Contact Person.

**Email Address:**

A read-only label auto-populated based on the selected Contact Person.

**CANCEL Button:**

Clicking the CANCEL button will close the dialog and not save any changes

**SAVE Button:**

Clicking the SAVE button will save any changes.`}
                    </GreenText>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button color='secondary' disabled={isLoading || isSubmitting} onClick={onClose}>
                  CANCEL
                </Button>

                <Button
                  color='primary'
                  disabled={isLoading || isSubmitting}
                  onClick={() => {
                    validateForm().then(formikErrors => {
                      setErrors(formikErrors)
                      setTouched(setNestedObjectValues(formikErrors, true))
                    })
                  }}
                  type='submit'
                  variant='contained'
                >
                  SAVE
                </Button>
              </DialogActions>
            </Form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
